import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Table, Input, Tag, Empty, Spin, Pagination, Typography, Alert,
  Popconfirm, Button, message,
} from 'antd';
import { SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import Cookies from 'js-cookie';
import Config from '../Config';
import { apiClient } from '../store/store';

const { Text } = Typography;
const config = new Config();

const NAVY = '#0f2847';
const NAVY_MID = '#1e4d8a';

// Warna Tag status disamakan dengan konvensi yang sudah dipakai di
// DataKoreksiNomorNotice.jsx (STATUS_COLOR/STATUS_LABEL), supaya konsisten
// di seluruh aplikasi.
const STATUS_COLOR = { 1: 'default', 2: 'blue', 3: 'green' };

const rp = (v) => `Rp ${Number(v || 0).toLocaleString('id-ID')}`;

/**
 * Modal detail transaksi hari ini, dibuka dari kartu "Pendaftaran/Penetapan/
 * Pembayaran Hari Ini" di dashboard. Mandiri (fetch API langsung, tidak lewat
 * redux) supaya bisa dipasang tanpa mengubah struktur Home.jsx yang sudah
 * kompleks - dipola sama seperti RealtimePenerimaan.jsx (apiClient langsung +
 * token dari cookie).
 */
export default function DashboardDetailModal({
  visible, onClose, statusKey, label, icon, warna, kabupatenParam, uptParam,
  canHapus, onDeleted,
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [totalNominal, setTotalNominal] = useState(0);
  const [tanggal, setTanggal] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [errMsg, setErrMsg] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const bisaHapus = !!canHapus && (statusKey === 'pendaftaran' || statusKey === 'penetapan');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchData = useCallback(async () => {
    if (!statusKey) return;
    setLoading(true);
    setErrMsg('');
    try {
      const token = Cookies.get(config.get_cookie_name());
      const params = new URLSearchParams();
      params.set('status', statusKey);
      params.set('limit', String(pageSize));
      params.set('offset', String((page - 1) * pageSize));
      if (search) params.set('search', search);
      if (kabupatenParam) params.set('kabupaten_id', kabupatenParam);
      if (uptParam) params.set('upt_id', uptParam);

      const response = await apiClient.get(
        `transaksi/api/dashboard/detail-hari-ini/?${params.toString()}`,
        { headers: { Authorization: token || '' } },
      );
      const data = response.data;
      setRows(data.results || []);
      setCount(data.count || 0);
      setTotalNominal(data.total_nominal || 0);
      setTanggal(data.tanggal || '');
    } catch (err) {
      setErrMsg('Gagal memuat data. Coba lagi.');
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [statusKey, page, pageSize, search, kabupatenParam, uptParam]);

  useEffect(() => {
    if (visible) fetchData();
  }, [visible, fetchData]);

  // Reset halaman & pencarian tiap kali modal dibuka utk kartu yang berbeda.
  useEffect(() => {
    if (visible) {
      setPage(1);
      setSearch('');
      setSearchInput('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, statusKey]);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleDelete = async (row) => {
    setDeletingId(row.id);
    try {
      const token = Cookies.get(config.get_cookie_name());
      await apiClient.delete(
        `transaksi/api/dashboard/hapus-transaksi/${row.id}/`,
        { headers: { Authorization: token || '' } },
      );
      message.success(`Transaksi ${row.nomor_polisi} berhasil dihapus.`);
      // Kalau ini item terakhir di halaman (bukan halaman 1), mundur satu
      // halaman supaya tidak nampilkan halaman kosong.
      if (rows.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchData();
      }
      if (onDeleted) onDeleted();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Gagal menghapus transaksi. Coba lagi.';
      message.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: 'No', width: 48, align: 'center',
      render: (_, __, idx) => (page - 1) * pageSize + idx + 1,
    },
    {
      title: 'Nomor Polisi', dataIndex: 'nomor_polisi', key: 'nomor_polisi', width: 140,
      render: (v) => <Text strong style={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>{v}</Text>,
    },
    { title: 'Nama Wajib Pajak', dataIndex: 'nama_wp', key: 'nama_wp', width: 200 },
    { title: 'UPT', dataIndex: 'upt_nama', key: 'upt_nama', width: 150 },
    ...(statusKey === 'penetapan' || statusKey === 'pembayaran' ? [{
      title: 'Petugas Penetapan', dataIndex: 'petugas_penetapan', key: 'petugas_penetapan', width: 170,
      render: (v) => v || '-',
    }] : []),
    ...(statusKey === 'pembayaran' ? [{
      title: 'Petugas Kasir', dataIndex: 'petugas_kasir', key: 'petugas_kasir', width: 170,
      render: (v) => v || '-',
    }] : []),
    {
      title: 'Nominal', dataIndex: 'nominal', key: 'nominal', align: 'right', width: 150,
      render: (v) => <Text strong style={{ color: NAVY_MID, fontFamily: 'monospace' }}>{rp(v)}</Text>,
    },
    {
      title: 'Waktu', key: 'waktu', width: 150,
      render: (_, row) => row.paid_on || row.tgl_penetapan || row.tgl_daftar || '-',
    },
    {
      title: 'Status', dataIndex: 'status_label', key: 'status_label', align: 'center', width: 120,
      render: (v, row) => <Tag color={STATUS_COLOR[row.status] || 'default'}>{v}</Tag>,
    },
    ...(bisaHapus ? [{
      title: 'Aksi', key: 'aksi', align: 'center', width: 90, fixed: 'right',
      render: (_, row) => (
        <Popconfirm
          title="Hapus transaksi ini?"
          description={`Nopol ${row.nomor_polisi} akan dihapus permanen dan tidak bisa dikembalikan.`}
          okText="Hapus"
          okType="danger"
          cancelText="Batal"
          onConfirm={() => handleDelete(row)}
        >
          <Button
            danger
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            loading={deletingId === row.id}
          />
        </Popconfirm>
      ),
    }] : []),
  ];

  const header = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      margin: '-20px -24px 16px', padding: '18px 24px',
      background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`,
    }}>
      <div style={{
        width: 46, height: 46, borderRadius: 12,
        background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22, color: '#fff', flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, lineHeight: 1.3 }}>{label}</div>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>
          {tanggal || 'Memuat tanggal...'}
        </div>
      </div>
    </div>
  );

  const summaryBar = (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
      justifyContent: 'space-between', marginBottom: 14,
    }}>
      <Tag color="blue" style={{ borderRadius: 20, padding: '2px 12px', fontWeight: 600 }}>
        {count} Transaksi
      </Tag>
      <Text style={{ fontSize: 13 }}>
        Total Nominal: <Text strong style={{ color: warna || NAVY_MID }}>{rp(totalNominal)}</Text>
      </Text>
    </div>
  );

  const searchBar = (
    <Input
      placeholder="Cari nomor polisi / nama wajib pajak..."
      prefix={<SearchOutlined style={{ color: '#aaa' }} />}
      value={searchInput}
      onChange={(e) => setSearchInput(e.target.value)}
      onPressEnter={handleSearch}
      onBlur={handleSearch}
      allowClear
      style={{ borderRadius: 8, marginBottom: 14 }}
    />
  );

  const mobileCards = (
    <div>
      {rows.length === 0 ? (
        <Empty description="Tidak ada data" style={{ padding: '24px 0' }} />
      ) : (
        rows.map((r, i) => (
          <div key={r.id || i} style={{
            border: '1px solid #e8edf5', borderRadius: 10, marginBottom: 8, overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(15,40,71,0.05)',
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #0f284709, #1e4d8a0d)', padding: '8px 12px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderBottom: '1px solid #e8edf5',
            }}>
              <Text strong style={{ fontFamily: 'monospace', fontSize: 13, color: NAVY, letterSpacing: 0.5 }}>
                {r.nomor_polisi}
              </Text>
              <Tag color={STATUS_COLOR[r.status] || 'default'} style={{ margin: 0 }}>{r.status_label}</Tag>
            </div>
            <div style={{ padding: '10px 12px' }}>
              <div style={{ fontSize: 13, color: '#334155', fontWeight: 600, marginBottom: 4 }}>{r.nama_wp}</div>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
                {r.upt_nama} · {r.paid_on || r.tgl_penetapan || r.tgl_daftar || '-'}
              </div>
              {(statusKey === 'penetapan' || statusKey === 'pembayaran') && r.petugas_penetapan && (
                <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>
                  Petugas Penetapan: <Text strong style={{ fontSize: 11 }}>{r.petugas_penetapan}</Text>
                </div>
              )}
              {statusKey === 'pembayaran' && r.petugas_kasir && (
                <div style={{ fontSize: 11, color: '#555', marginBottom: 6 }}>
                  Petugas Kasir: <Text strong style={{ fontSize: 11 }}>{r.petugas_kasir}</Text>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: NAVY_MID, fontFamily: 'monospace' }}>
                  {rp(r.nominal)}
                </div>
                {bisaHapus && (
                  <Popconfirm
                    title="Hapus transaksi ini?"
                    description={`Nopol ${r.nomor_polisi} akan dihapus permanen.`}
                    okText="Hapus"
                    okType="danger"
                    cancelText="Batal"
                    onConfirm={() => handleDelete(r)}
                  >
                    <Button
                      danger
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={deletingId === r.id}
                    >
                      Hapus
                    </Button>
                  </Popconfirm>
                )}
              </div>
            </div>
          </div>
        ))
      )}
      {count > pageSize && (
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Pagination
            simple
            current={page}
            pageSize={pageSize}
            total={count}
            onChange={(p) => setPage(p)}
          />
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '96vw' : '96vw'}
      style={{ maxWidth: 1720, top: isMobile ? 8 : 16 }}
      bodyStyle={{ padding: '20px 24px', maxHeight: '82vh', overflowY: 'auto' }}
      destroyOnClose
    >
      {header}

      {errMsg && <Alert type="error" message={errMsg} showIcon style={{ marginBottom: 14 }} />}

      {searchBar}
      {summaryBar}

      <Spin spinning={loading}>
        {isMobile ? (
          mobileCards
        ) : (
          <Table
            rowKey={(r) => r.id}
            columns={columns}
            dataSource={rows}
            size="small"
            bordered
            locale={{ emptyText: <Empty description="Tidak ada data" /> }}
            pagination={{
              current: page,
              pageSize,
              total: count,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50'],
              showTotal: (total) => `Total ${total} transaksi`,
              onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            }}
            scroll={{ x: columns.reduce((sum, c) => sum + (c.width || 120), 0) }}
          />
        )}
      </Spin>
    </Modal>
  );
}
